import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, EyeOff, LogOut, Siren, FileText, Loader2 } from 'lucide-react';
import { CandidatLink } from '../candidat/CandidatLink';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fr = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR') : '—';

// Bouton d'export Word du rapport « candidats en difficulté » (regroupé par formation)
export const ExportDifficultesWord = () => {
  const [loading, setLoading] = useState(false);
  const exporter = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/dashboard/candidats-en-cours/difficultes.docx`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `candidats_en_difficulte_${new Date().toISOString().slice(0, 10)}.docx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Échec de l'export Word"); } finally { setLoading(false); }
  };
  return (
    <button type="button" onClick={exporter} disabled={loading} data-testid="difficultes-export-word"
      className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50">
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Exporter en Word
    </button>
  );
};

const CandidatDifficulte = ({ e, onAbandon, onIgnorer }) => (
  <div className="border border-red-100 bg-red-50/40 rounded-lg p-3" data-testid={`difficulte-row-${e.candidat_id}-${e.dossier_id}`}>
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <CandidatLink dossierId={e.dossier_id} candidatId={e.candidat_id} className="text-sm font-semibold">{e.candidat_nom}</CandidatLink>
      <div className="flex items-center gap-1.5">
        {e.nb_na > 0 && <span className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">{e.nb_na} NA</span>}
        {e.nb_absences > 0 && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">{e.nb_absences} absence(s)</span>}
        {e.suspicion_abandon && <span className="text-[10px] font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`badge-suspicion-diff-${e.candidat_id}`}><Siren className="w-3 h-3" /> Suspicion d'abandon</span>}
        <button type="button" onClick={() => onAbandon(e)} className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-semibold text-red-700 bg-white border border-red-200 hover:bg-red-600 hover:text-white transition-colors" title="Déclarer l'abandon" data-testid={`declarer-abandon-${e.candidat_id}-${e.dossier_id}`}><LogOut className="w-3 h-3" /> Abandon</button>
        <button type="button" onClick={() => onIgnorer(e)} className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-700 hover:text-white transition-colors" title="Lever la difficulté (motif obligatoire, tracé sur la fiche du candidat)" data-testid={`ignorer-difficulte-${e.candidat_id}-${e.dossier_id}`}><EyeOff className="w-3 h-3" /> Lever</button>
      </div>
    </div>
    {(e.na_details || []).length > 0 && (
      <div className="mt-2 space-y-0.5">
        <p className="text-[10px] uppercase font-semibold text-red-500 tracking-wide">Compétences non acquises</p>
        {e.na_details.map((d, di) => (
          <p key={`${d.competence}-${di}`} className="text-xs text-slate-600">• {(d.competence || d.test_titre || '').trim()}{d.competence && d.test_titre ? <span className="text-slate-400"> — {d.test_titre}</span> : null}</p>
        ))}
      </div>
    )}
    {e.suspicion_abandon && (
      <div className="mt-2 rounded-md bg-orange-50 border border-orange-100 px-2.5 py-1.5" data-testid={`suspicion-detail-${e.candidat_id}`}>
        <p className="text-[10px] uppercase font-semibold text-orange-600 tracking-wide">Signalé par {e.suspicion_abandon.signale_par_nom} le {fr(e.suspicion_abandon.date)}</p>
        <p className="text-xs text-slate-700 whitespace-pre-wrap">{e.suspicion_abandon.motif}</p>
      </div>
    )}
    {(e.absences_dates || []).length > 0 && (
      <div className="mt-2">
        <p className="text-[10px] uppercase font-semibold text-amber-600 tracking-wide">Absences</p>
        <p className="text-xs text-slate-600">{e.absences_dates.map(fr).join(' · ')}</p>
      </div>
    )}
  </div>
);

// Liste des candidats en difficulté regroupés par action de formation
export const DifficultesParFormation = ({ entries, onAbandon, onIgnorer }) => {
  const groupes = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      if (!m.has(e.dossier_id)) m.set(e.dossier_id, { id: e.dossier_id, code: e.dossier_code, intitule: e.dossier_intitule, candidats: [] });
      m.get(e.dossier_id).candidats.push(e);
    }
    return [...m.values()].sort((a, b) => a.code.localeCompare(b.code));
  }, [entries]);
  if (groupes.length === 0) return <p className="text-sm text-slate-400 italic py-4 text-center">Aucun candidat en difficulté</p>;
  return (
    <div className="space-y-4" data-testid="difficultes-par-formation">
      {groupes.map(g => (
        <section key={g.id} data-testid={`difficultes-groupe-${g.id}`}>
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-1 mb-2">
            <Link to={`/dossiers/${g.id}`} className="flex items-center gap-2 min-w-0 hover:underline">
              <span className="text-[10px] font-mono font-bold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded shrink-0">{g.code}</span>
              <span className="text-sm font-semibold text-slate-800 truncate">{g.intitule}</span>
            </Link>
            <span className="text-[11px] text-red-600 font-semibold shrink-0 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {g.candidats.length} candidat(s)</span>
          </div>
          <div className="space-y-2">
            {[...g.candidats].sort((a, b) => a.candidat_nom.localeCompare(b.candidat_nom)).map(e => <CandidatDifficulte key={`${e.candidat_id}-${e.dossier_id}`} e={e} onAbandon={onAbandon} onIgnorer={onIgnorer} />)}
          </div>
        </section>
      ))}
    </div>
  );
};
